import {
  canShareTaskWithHousehold,
  CREATE_TASK_NEXT_STEPS_OPTIONS,
  evaluateClientQuota,
  getOtherHouseholdMembers,
} from '@frontend/api-client';
import { safeStringConcat } from '@frontend/duck-tape';
import { Fragment, useEffect, useMemo, useState } from '@frontend/react';
import {
  Button,
  CheckboxInput,
  Divider,
  Icon,
  RadioInput,
  Tag,
  Text,
  useShortcut,
  XStack,
  YStack,
} from '@frontend/web-react';
import { safeCss } from '@frontend/web-utils';
import {
  useAnalytics,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
  useRetrieveHouseholdQuery,
} from '@frontend/web/hooks';
import { useCreateTaskContext } from '@frontend/web/hooks/context/useCreateTaskContext';
import { TaskPreview } from './helpers/TaskPreview';

export const NEXT_STEPS_QUESTION = 'How should we approach this task?';

export const NextSteps = () => {
  const { track } = useAnalytics();
  const {
    closeCreateTaskModal,
    createTaskState,
    decrementStep,
    incrementStep,
    initialQuestions,
    isStreaming,
    isSubmitting,
    updateTaskData,
  } = useCreateTaskContext();
  const { nextSteps } = createTaskState.taskData;
  const [isStepComplete, setIsStepComplete] = useState(!!nextSteps);
  const canProceed = isStreaming ? !!initialQuestions.length : true;
  const isSubmitDisabled = !isStepComplete;
  const { data: clientStatus } = useRetrieveClientStatusQuery();
  const isDraftTask = clientStatus && evaluateClientQuota(clientStatus).isThrottled && !createTaskState.isRecurrence;
  const { data: client } = useRetrieveClientQuery();
  const { data: householdDetails } = useRetrieveHouseholdQuery(client?.household as string);

  const [sharedWithHouseholdMembers, setSharedWithHouseholdMembers] = useState(false);
  const householdMembers = getOtherHouseholdMembers(client?.id, householdDetails);
  const hasMultipleHouseholdMembers = (householdMembers.length ?? 0) > 1;
  const firstHouseholdMember = householdMembers[0];
  const displayShareWithHousehold = useMemo(
    () =>
      client &&
      clientStatus &&
      householdDetails &&
      canShareTaskWithHousehold({
        client,
        clientStatus,
        householdDetails,
      }),
    [client, clientStatus, householdDetails],
  );
  const handleShareWithHouseholdChange = () => {
    // If deselecting, remove all sharedWithClientIds
    if (createTaskState.taskData.sharedWithClientIds.length) {
      updateTaskData({ sharedWithClientIds: [] });
      setSharedWithHouseholdMembers(false);
    } else if (!hasMultipleHouseholdMembers && firstHouseholdMember?.id) {
      // If selecting, automatically add the first household member
      updateTaskData({
        sharedWithClientIds: [firstHouseholdMember.id],
      });
      setSharedWithHouseholdMembers(true);
    } else {
      setSharedWithHouseholdMembers(true);
    }
  };

  const handleHouseholdMemberTagPress = (memberId: string) => {
    const updatedSharedWithClientIds = createTaskState.taskData.sharedWithClientIds?.includes(memberId)
      ? createTaskState.taskData.sharedWithClientIds.filter((id) => id !== memberId)
      : [...(createTaskState.taskData.sharedWithClientIds ?? []), memberId];
    updateTaskData({ sharedWithClientIds: updatedSharedWithClientIds });
  };

  const renderHouseholdMemberTags = () => {
    if (!hasMultipleHouseholdMembers || !sharedWithHouseholdMembers) return null;

    return (
      <XStack className="flex-wrap gap-sm p-md pt-none">
        {householdMembers.map((hhMember) => (
          <Tag
            activeContainerClassName={safeCss('bg-surfaceBrand')}
            activeIconColor="inverse"
            activeTextColor="inverse"
            className="px-sm py-xs"
            isActive={createTaskState.taskData.sharedWithClientIds?.includes(hhMember.id)}
            key={hhMember.id}
            label={hhMember.name}
            leftIconName={
              createTaskState.taskData.sharedWithClientIds?.includes(hhMember.id) ? 'IconCheck' : 'IconPlus'
            }
            leftIconSize="sm"
            onClick={() => handleHouseholdMemberTagPress(hhMember.id)}
          />
        ))}
      </XStack>
    );
  };

  useEffect(() => {
    track({
      data: {
        aiGenerated: false,
        question: NEXT_STEPS_QUESTION,
        taskDescription: createTaskState.taskData.description,
      },
      event: 'viewedTaskCreationQuestion',
      topic: 'tasks',
    });
  }, []);

  useEffect(() => {
    setIsStepComplete(!!nextSteps);
  }, [nextSteps]);

  useShortcut({
    Enter: {
      isDisabled: isSubmitDisabled,
      onSubmit: incrementStep,
    },
  });

  return (
    <YStack>
      <XStack className="p-md" justifyContent="center">
        {createTaskState.currentStep !== createTaskState.steps[0] ? (
          <Icon name="IconArrowLeft" onClick={decrementStep} size="lg" />
        ) : null}
        <XStack alignItems="center" className="flex-1" justifyContent="center">
          <Text color="secondary" textAlign="center" type="h5">
            {isDraftTask ? 'Create Draft' : 'Send to Copilot'}
          </Text>
        </XStack>
        <Icon name="IconX" onClick={closeCreateTaskModal} size="lg" />
      </XStack>
      <Divider />
      <YStack>
        <YStack className="px-md pt-md">
          <TaskPreview />
        </YStack>
        <YStack className="px-md py-md">
          <Text type="h5">{NEXT_STEPS_QUESTION}</Text>
        </YStack>
        <RadioInput
          onChange={(nextSteps) => {
            updateTaskData({ nextSteps });
          }}
          options={CREATE_TASK_NEXT_STEPS_OPTIONS.map((o) => ({
            description: o.description,
            id: o.title,
            label: o.title,
            value: o.value,
          }))}
          value={nextSteps}
        />
      </YStack>

      <Divider />
      {displayShareWithHousehold ? (
        <Fragment>
          <YStack>
            <YStack>
              <CheckboxInput
                className="p-md"
                label={
                  hasMultipleHouseholdMembers
                    ? 'Share with...'
                    : safeStringConcat('Share with ', firstHouseholdMember?.name)
                }
                onChange={handleShareWithHouseholdChange}
                value={sharedWithHouseholdMembers}
              />
            </YStack>
            {renderHouseholdMemberTags()}
          </YStack>
          <Divider />
        </Fragment>
      ) : null}
      <YStack className="p-md" gapY="sm" justifyContent="end">
        <Button
          color="primary"
          disabled={isSubmitDisabled}
          isLoading={isSubmitting || !canProceed}
          label="Proceed with task"
          noBorder
          onClick={incrementStep}
          radius="xl"
        />
      </YStack>
    </YStack>
  );
};
